import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { Lightbulb, Plus, ThumbsUp, Clock, CheckCircle, XCircle, AlertCircle, MessageSquare, X, ChevronDown, ChevronUp, Send, Shield } from 'lucide-react';

const STATUS_CONFIG = {
  submitted: { label: 'Submitted', color: '#3b82f6', bg: '#dbeafe', icon: Clock },
  under_review: { label: 'Under Review', color: '#f59e0b', bg: '#fef3c7', icon: AlertCircle },
  planned: { label: 'Planned', color: '#7c3aed', bg: '#f3e8ff', icon: Lightbulb },
  in_progress: { label: 'In Progress', color: '#2563eb', bg: '#dbeafe', icon: Clock },
  completed: { label: 'Completed', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
  declined: { label: 'Declined', color: '#dc2626', bg: '#fef2f2', icon: XCircle },
};

const CATEGORIES = ['general', 'ai_agents', 'integrations', 'reporting', 'security', 'ui_ux', 'billing', 'other'];

export default function FeatureRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [comments, setComments] = useState({});
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const isStaff = user?.role === 'owner' || user?.role === 'panzix_employee';

  useEffect(() => { loadRequests(); }, []);

  const loadRequests = async () => {
    try { const r = await api.get('/api/feature-requests'); setRequests(r.data || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadComments = async (frId) => {
    try { const r = await api.get(`/api/feature-requests/${frId}/comments`); setComments(prev => ({ ...prev, [frId]: r.data || [] })); }
    catch (e) { console.error(e); }
  };

  const handleAddComment = async (frId) => {
    if (!commentText.trim()) return;
    setSendingComment(true);
    try {
      await api.post(`/api/feature-requests/${frId}/comments`, { content: commentText });
      setCommentText('');
      loadComments(frId);
    } catch (e) { console.error(e); }
    finally { setSendingComment(false); }
  };

  const handleExpand = (frId) => {
    if (expandedId === frId) { setExpandedId(null); return; }
    setExpandedId(frId);
    loadComments(frId);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/api/feature-requests', { title, description, category, priority });
      setTitle(''); setDescription(''); setCategory('general'); setPriority('normal');
      setShowNew(false); loadRequests();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  const handleVote = async (id) => {
    try { await api.post(`/api/feature-requests/${id}/vote`); loadRequests(); }
    catch (e) { console.error(e); }
  };

  const handleUpdateStatus = async (id) => {
    try {
      await api.patch(`/api/feature-requests/${id}`, { status: editStatus, admin_notes: editNotes });
      setEditingId(null); loadRequests();
    } catch (e) { console.error(e); }
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const statusCounts = {};
  requests.forEach(r => { statusCounts[r.status] = (statusCounts[r.status] || 0) + 1; });

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in" style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Feature Requests</h3>
          <p style={{ fontSize: 14, color: '#64748b' }}>{isStaff ? 'Review and manage feature requests from subscribers.' : 'Request new features and track their status.'}</p>
        </div>
        {!isStaff && (
          <button className="btn-action" onClick={() => setShowNew(true)} data-testid="new-request-btn">
            <Plus size={16} style={{ marginRight: 6 }} />New Request
          </button>
        )}
      </div>

      {/* Status Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={() => setFilter('all')} className={filter === 'all' ? 'btn-action' : 'btn-secondary'} style={{ padding: '6px 14px', fontSize: 12 }} data-testid="filter-all">
          All ({requests.length})
        </button>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <button key={key} onClick={() => setFilter(key)} style={{ padding: '6px 14px', fontSize: 12, borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600,
            background: filter === key ? cfg.bg : '#f1f5f9', color: filter === key ? cfg.color : '#64748b' }} data-testid={`filter-${key}`}>
            {cfg.label} ({statusCounts[key] || 0})
          </button>
        ))}
      </div>

      {/* Requests List */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <Lightbulb size={64} />
          <h4>No Feature Requests</h4>
          <p>{isStaff ? 'No requests from subscribers yet.' : 'Be the first to request a feature!'}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(fr => {
            const sc = STATUS_CONFIG[fr.status] || STATUS_CONFIG.submitted;
            const StatusIcon = sc.icon;
            const isExpanded = expandedId === fr.id;
            return (
              <div key={fr.id} style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }} data-testid={`request-${fr.id}`}>
                <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  {/* Vote */}
                  <button onClick={() => handleVote(fr.id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, background: 'none', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', minWidth: 48 }} data-testid={`vote-${fr.id}`}>
                    <ThumbsUp size={16} color={fr.voters?.includes(user?.id) ? 'var(--panzix-green)' : '#94a3b8'} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{fr.votes || 0}</span>
                  </button>

                  {/* Content */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <h4 style={{ fontSize: 15, fontWeight: 700 }}>{fr.title}</h4>
                      <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <StatusIcon size={12} />{sc.label}
                      </span>
                      <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, background: '#f1f5f9', color: '#64748b', textTransform: 'capitalize' }}>{fr.category}</span>
                    </div>
                    <p style={{ fontSize: 12, color: '#64748b' }}>
                      {isStaff && fr.company_name && <><strong>{fr.company_name}</strong> &middot; </>}
                      {fr.submitted_by_name} &middot; {new Date(fr.created_at).toLocaleDateString()}
                      {fr.priority !== 'normal' && <span style={{ marginLeft: 8, color: fr.priority === 'high' ? '#dc2626' : fr.priority === 'urgent' ? '#7c2d12' : '#64748b', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>{fr.priority}</span>}
                    </p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {isStaff && (
                      <button onClick={() => { setEditingId(fr.id); setEditStatus(fr.status); setEditNotes(fr.admin_notes || ''); }} className="btn-secondary" style={{ padding: '6px 10px', fontSize: 11 }} data-testid={`manage-${fr.id}`}>
                        <MessageSquare size={12} style={{ marginRight: 4 }} />Manage
                      </button>
                    )}
                    <button onClick={() => handleExpand(fr.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                      {isExpanded ? <ChevronUp size={16} color="#64748b" /> : <ChevronDown size={16} color="#64748b" />}
                    </button>
                  </div>
                </div>

                {/* Expanded with Comments */}
                {isExpanded && (
                  <div style={{ padding: '0 20px 16px', borderTop: '1px solid #f1f5f9' }}>
                    <div style={{ paddingTop: 14 }}>
                      <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{fr.description}</p>
                      {fr.admin_notes && (
                        <div style={{ marginTop: 12, padding: 12, background: '#f8fafc', borderRadius: 8, borderLeft: '3px solid var(--panzix-purple)' }}>
                          <p style={{ fontSize: 11, fontWeight: 700, color: '#7B1FA2', marginBottom: 4 }}>Panzix Team Response:</p>
                          <p style={{ fontSize: 13, color: '#374151' }}>{fr.admin_notes}</p>
                        </div>
                      )}
                      
                      {/* Comments Section */}
                      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
                        <h5 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <MessageSquare size={14} />Comments ({(comments[fr.id] || []).length})
                        </h5>
                        
                        {(comments[fr.id] || []).length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                            {(comments[fr.id] || []).map(c => (
                              <div key={c.id} style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: 8, borderLeft: (c.user_role === 'owner' || c.user_role === 'panzix_employee') ? '3px solid #7B1FA2' : '3px solid #e2e8f0' }}>
                                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                  {(c.user_role === 'owner' || c.user_role === 'panzix_employee') && <Shield size={10} color="#7B1FA2" />}
                                  <strong>{c.user_name}</strong>
                                  {(c.user_role === 'owner' || c.user_role === 'panzix_employee') && <span style={{ color: '#7B1FA2', fontSize: 9 }}>Panzix Team</span>}
                                  <span>&middot; {new Date(c.created_at).toLocaleString()}</span>
                                </div>
                                <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{c.content}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Add Comment */}
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input value={commentText} onChange={e => setCommentText(e.target.value)} 
                            onKeyDown={e => e.key === 'Enter' && handleAddComment(fr.id)}
                            placeholder="Add a comment..." 
                            style={{ flex: 1, padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13, outline: 'none' }} 
                            data-testid={`comment-input-${fr.id}`} />
                          <button onClick={() => handleAddComment(fr.id)} disabled={sendingComment || !commentText.trim()} 
                            className="btn-action" style={{ padding: '10px 16px' }} data-testid={`send-comment-${fr.id}`}>
                            <Send size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New Request Modal */}
      {showNew && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setShowNew(false)}>
          <div className="task-modal slide-up" style={{ maxWidth: 540 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>Submit Feature Request</h3>
              <button onClick={() => setShowNew(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="form-group">
              <label>Title</label>
              <input data-testid="fr-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Brief title for your feature request" />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Category</label>
                <select data-testid="fr-category" value={category} onChange={e => setCategory(e.target.value)} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14 }}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Priority</label>
                <select data-testid="fr-priority" value={priority} onChange={e => setPriority(e.target.value)} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14 }}>
                  <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea data-testid="fr-description" value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe the feature you'd like to see. Include use cases and expected behavior."
                style={{ width: '100%', minHeight: 120, padding: 12, border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
            <button className="btn-action" onClick={handleSubmit} disabled={submitting || !title.trim() || !description.trim()} style={{ width: '100%' }} data-testid="submit-request">
              {submitting ? 'Submitting...' : <><Lightbulb size={14} style={{ marginRight: 6 }} />Submit Request</>}
            </button>
          </div>
        </div>
      )}

      {/* Manage Request Modal (Staff) */}
      {editingId && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setEditingId(null)}>
          <div className="task-modal slide-up" style={{ maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>Manage Request</h3>
              <button onClick={() => setEditingId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select data-testid="edit-fr-status" value={editStatus} onChange={e => setEditStatus(e.target.value)} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14 }}>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => <option key={key} value={key}>{cfg.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Response / Notes to Subscriber</label>
              <textarea data-testid="edit-fr-notes" value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Add a note visible to the subscriber about this request..."
                style={{ width: '100%', minHeight: 100, padding: 12, border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
            <button className="btn-action purple" onClick={() => handleUpdateStatus(editingId)} style={{ width: '100%' }} data-testid="save-fr-update">
              Update Request
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
